import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaBars, FaTimes, FaChevronDown } from "react-icons/fa";
import "./Navbar.css";
import { ENQUIRY_URL, ADMISSION_ITEMS } from "../config";

const campusItems = [
  { id: "rajaji-nagar", label: "Rajajinagar Campus" },
  { id: "bashveshwarnagar", label: "Basaveshwaranagar Campus" },
  { id: "macholli", label: "Macholli Campus" },
  { id: "naavruja", label: "Naavruja Campus" },
];

const registerItems = [
  { label: "Register", href: ENQUIRY_URL, external: true },
  {
    label: "Parent Login",
    href: "https://parent.opus-svect.com/Login",
    external: true,
  },
  { label: "FAQ", href: "/faq", external: false },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [campusDropdownOpen, setCampusDropdownOpen] = useState(false);
  const [registerDropdownOpen, setRegisterDropdownOpen] = useState(false);
  const [mobileCampusOpen, setMobileCampusOpen] = useState(false);
  const [mobileRegisterOpen, setMobileRegisterOpen] = useState(false);
  const [admissionDropdownOpen, setAdmissionDropdownOpen] = useState(false);
  const [mobileAdmissionOpen, setMobileAdmissionOpen] = useState(false);
  const admissionRef = useRef(null);
  const campusRef = useRef(null);
  const registerRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (campusRef.current && !campusRef.current.contains(e.target))
        setCampusDropdownOpen(false);
      if (registerRef.current && !registerRef.current.contains(e.target))
        setRegisterDropdownOpen(false);
      if (admissionRef.current && !admissionRef.current.contains(e.target))
        setAdmissionDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const closeMenu = () => {
    setIsOpen(false);
    setMobileCampusOpen(false);
    setMobileRegisterOpen(false);
    setMobileAdmissionOpen(false);
  };

  const handleCampusSelect = (id) => {
    setCampusDropdownOpen(false);
    closeMenu();
    navigate(`/campuses/${id}`);
  };

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About Us" },
    { path: "/academics", label: "Academics" },
  ];

  const navLinksAfterRegister = [
    { path: "/student-life", label: "Student Life" },
    { path: "/news-events", label: "News & Events" },
    { path: "/gallery", label: "Gallery" },
    { path: "/contact", label: "Contact Us" },
  ];

  return (
    <nav className={`navbar ${scrolled ? "scrolled" : ""}`}>
      <div className="container navbar-container">
        <Link to="/" className="navbar-logo" onClick={closeMenu}>
          <div className="logo-wrapper">
            <img
              src="/logo.png"
              alt="Sri Vani Education Logo"
              className="logo-image"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            <div className="logo-content">
              <h1>Sri Vani</h1>
              <p>Education Since 1966</p>
            </div>
          </div>
        </Link>

        <ul className={`nav-menu ${isOpen ? "active" : ""}`}>
          {navLinks.map((link) => (
            <li key={link.path} className="nav-item">
              <Link
                to={link.path}
                className={`nav-link ${location.pathname === link.path ? "active" : ""}`}
                onClick={closeMenu}
              >
                {link.label}
              </Link>
            </li>
          ))}

          {/* Our Campuses — desktop hover dropdown */}
          <li
            className={`nav-item nav-item--dropdown desktop-dropdown-item ${location.pathname.startsWith("/campuses") ? "active-parent" : ""}`}
            ref={campusRef}
            onMouseEnter={() => setCampusDropdownOpen(true)}
            onMouseLeave={() => setCampusDropdownOpen(false)}
          >
            <button
              className={`nav-link nav-link--dropdown-trigger ${location.pathname.startsWith("/campuses") ? "active" : ""}`}
              onClick={() => setCampusDropdownOpen((prev) => !prev)}
            >
              Our Campuses{" "}
              <FaChevronDown
                className={`dropdown-chevron ${campusDropdownOpen ? "open" : ""}`}
              />
            </button>
            {campusDropdownOpen && (
              <ul className="campus-dropdown">
                {campusItems.map((c) => (
                  <li key={c.id}>
                    <button
                      className="campus-dropdown-item"
                      onClick={() => handleCampusSelect(c.id)}
                    >
                      {c.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </li>

          {/* Our Campuses — mobile accordion */}
          <li className="nav-item mobile-campus-item">
            <button
              className="nav-link nav-link--accordion"
              onClick={() => setMobileCampusOpen(!mobileCampusOpen)}
            >
              Our Campuses{" "}
              <FaChevronDown
                className={`dropdown-chevron ${mobileCampusOpen ? "open" : ""}`}
              />
            </button>
            {mobileCampusOpen && (
              <ul className="campus-mobile-list">
                <li>
                  <Link
                    to="/campuses"
                    className="campus-mobile-item"
                    onClick={closeMenu}
                  >
                    View All Campuses
                  </Link>
                </li>
                {campusItems.map((c) => (
                  <li key={c.id}>
                    <button
                      className="campus-mobile-item"
                      onClick={() => handleCampusSelect(c.id)}
                    >
                      {c.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </li>

          {/* Register / Login — desktop dropdown */}
          <li
            className="nav-item nav-item--dropdown desktop-dropdown-item"
            ref={registerRef}
            onMouseEnter={() => setRegisterDropdownOpen(true)}
            onMouseLeave={() => setRegisterDropdownOpen(false)}
          >
            <button
              className="nav-link nav-link--dropdown-trigger"
              onClick={() => setRegisterDropdownOpen((prev) => !prev)}
            >
              Register – Login{" "}
              <FaChevronDown
                className={`dropdown-chevron ${registerDropdownOpen ? "open" : ""}`}
              />
            </button>
            {registerDropdownOpen && (
              <ul className="campus-dropdown">
                {registerItems.map((item) => (
                  <li key={item.label}>
                    {item.external ? (
                      <a
                        href={item.href}
                        className="campus-dropdown-item"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setRegisterDropdownOpen(false)}
                      >
                        {item.label}
                      </a>
                    ) : (
                      <Link
                        to={item.href}
                        className="campus-dropdown-item"
                        onClick={() => setRegisterDropdownOpen(false)}
                      >
                        {item.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </li>

          {/* Register / Login — mobile accordion */}
          <li className="nav-item mobile-campus-item">
            <button
              className="nav-link nav-link--accordion"
              onClick={() => setMobileRegisterOpen(!mobileRegisterOpen)}
            >
              Register – Login{" "}
              <FaChevronDown
                className={`dropdown-chevron ${mobileRegisterOpen ? "open" : ""}`}
              />
            </button>
            {mobileRegisterOpen && (
              <ul className="campus-mobile-list">
                {registerItems.map((item) => (
                  <li key={item.label}>
                    {item.external ? (
                      <a
                        href={item.href}
                        className="campus-mobile-item"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={closeMenu}
                      >
                        {item.label}
                      </a>
                    ) : (
                      <Link
                        to={item.href}
                        className="campus-mobile-item"
                        onClick={closeMenu}
                      >
                        {item.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </li>

          {navLinksAfterRegister.map((link) => (
            <li key={link.path} className="nav-item">
              <Link
                to={link.path}
                className={`nav-link ${location.pathname === link.path ? "active" : ""}`}
                onClick={closeMenu}
              >
                {link.label}
              </Link>
            </li>
          ))}

          <li className="nav-item mobile-only">
            <button
              className="btn btn-primary"
              onClick={() => setMobileAdmissionOpen(!mobileAdmissionOpen)}
            >
              Admission Enquiry{" "}
              <FaChevronDown
                className={`dropdown-chevron ${mobileAdmissionOpen ? "open" : ""}`}
              />
            </button>
            {mobileAdmissionOpen && (
              <ul className="campus-mobile-list">
                {ADMISSION_ITEMS.map((item) => (
                  <li key={item.label}>
                    <a
                      href={item.href}
                      className="campus-mobile-item"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={closeMenu}
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </li>
        </ul>

        <div
          className="nav-cta desktop-only nav-item--dropdown"
          ref={admissionRef}
          onMouseEnter={() => setAdmissionDropdownOpen(true)}
          onMouseLeave={() => setAdmissionDropdownOpen(false)}
        >
          <button
            className="btn btn-primary btn-admission"
            onClick={() => setAdmissionDropdownOpen((prev) => !prev)}
          >
            Admission Enquiry{" "}
            <FaChevronDown
              className={`dropdown-chevron ${admissionDropdownOpen ? "open" : ""}`}
            />
          </button>
          {admissionDropdownOpen && (
            <ul className="campus-dropdown campus-dropdown--right">
              {ADMISSION_ITEMS.map((item) => (
                <li key={item.label}>
                  <a
                    href={item.href}
                    className="campus-dropdown-item"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setAdmissionDropdownOpen(false)}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="menu-icon" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <FaTimes /> : <FaBars />}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
